/*
 * <gs-header> — controller for sections/gs-header.liquid.
 *
 * Owns: panel open/close (mega / search / mobile), Esc + outside-click, focus
 * return, scroll lock, mobile category switching, small dropdowns, cart count,
 * announcement code copy. Mega menu (Task 2) and search (Task 3) plug in via
 * the events / methods documented in docs/gs-header-architecture.md.
 */
if (!customElements.get('gs-header')) {
  /* Matches assets/gs-header.css's mobile/tablet breakpoint (max-width: 1360px). */
  const DESKTOP_QUERY = '(min-width: 1361px)';

  class GSHeader extends HTMLElement {
    constructor() {
      super();
      this.activePanel = null;
      this._lastTrigger = null;
      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onBreakpoint = this._onBreakpoint.bind(this);
      this._onCartUpdate = this._onCartUpdate.bind(this);
      this._onResize = this._onResize.bind(this);
    }

    connectedCallback() {
      this.shell = this.querySelector('[data-gs-header-shell]');
      this.addEventListener('click', this._onClick);
      document.addEventListener('keydown', this._onKeydown);
      document.addEventListener('pointerdown', this._onPointerDown);
      this._mq = window.matchMedia(DESKTOP_QUERY);
      this._mq.addEventListener('change', this._onBreakpoint);
      window.addEventListener('resize', this._onResize);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, this._onCartUpdate);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      document.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('pointerdown', this._onPointerDown);
      if (this._mq) this._mq.removeEventListener('change', this._onBreakpoint);
      window.removeEventListener('resize', this._onResize);
      if (this._unsubscribeCart) this._unsubscribeCart();
      this._setScrollLock(false);
    }

    /* ---------- public API ---------- */

    getPanel(name) {
      return this.querySelector(`[data-gs-header-panel="${name}"]`);
    }

    /** Open a panel by name ('mega' | 'search' | 'mobile' | any data-gs-header-panel). */
    openPanel(name, { trigger = null, focus = true } = {}) {
      const panel = this.getPanel(name);
      if (!panel) return false;
      if (this.activePanel === name) return true;
      if (this.activePanel) this.closePanel({ restoreFocus: false });
      this._closeDropdowns();

      this._lastTrigger = trigger || this.querySelector(`[data-gs-header-panel-trigger="${name}"]`);
      this._setPanelHidden(panel, false);
      this._syncMobileOffset(panel);
      panel.classList.add('is-open');
      this.activePanel = name;
      this.dataset.gsHeaderState = name;
      this._syncTriggers();
      if (panel.hasAttribute('data-gs-header-lock-scroll')) this._setScrollLock(true);

      if (name === 'mobile' && focus && this._lastTrigger) this._lastTrigger.focus();
      this.dispatchEvent(new CustomEvent('gs-header:open', { bubbles: true, detail: { panel: name, element: panel, trigger: this._lastTrigger } }));
      return true;
    }

    closePanel({ restoreFocus = true } = {}) {
      const name = this.activePanel;
      if (!name) return;
      const panel = this.getPanel(name);
      const hadFocus = panel && panel.contains(document.activeElement);
      if (panel) {
        this._setPanelHidden(panel, true);
        panel.classList.remove('is-open');
      }
      this.activePanel = null;
      this.dataset.gsHeaderState = 'closed';
      this._syncTriggers();
      this._setScrollLock(false);

      if (restoreFocus && this._lastTrigger && (hadFocus || name === 'mobile')) {
        this._lastTrigger.focus();
      }
      this.dispatchEvent(new CustomEvent('gs-header:close', { bubbles: true, detail: { panel: name, element: panel } }));
    }

    togglePanel(name, opts) {
      return this.activePanel === name ? this.closePanel() : this.openPanel(name, opts);
    }

    /* ---------- internals ---------- */

    _syncTriggers() {
      this.querySelectorAll('[data-gs-header-panel-trigger]').forEach((t) => {
        const open = t.dataset.gsHeaderPanelTrigger === this.activePanel;
        t.setAttribute('aria-expanded', String(open));
        if (t.hasAttribute('data-label-open')) {
          t.setAttribute('aria-label', open ? t.dataset.labelClose : t.dataset.labelOpen);
        }
      });
      const searchInput = this.querySelector('[data-gs-header-search-input]');
      if (searchInput) searchInput.setAttribute('aria-expanded', String(this.activePanel === 'search'));
    }

    _setScrollLock(on) {
      document.documentElement.classList.toggle('gs-hdr-scroll-locked', on);
    }

    /* Panels marked data-gs-header-animated (the fullscreen mobile sheet) never
       use [hidden] — their CSS visibility/transform pair (assets/gs-header.css
       .gs-hdr__panel--mobile) handles both the slide and inertness, mirroring
       .drawer in assets/component-cart-drawer.css. Toggling [hidden] there
       would kill the transition by jumping straight to display:none. */
    _setPanelHidden(panel, hidden) {
      if (panel.hasAttribute('data-gs-header-animated')) return;
      panel.hidden = hidden;
    }

    /* Keeps the fullscreen mobile sheet's content clear of the floating header
       bar (which stays visible above it) without hardcoding the bar's height. */
    _syncMobileOffset(panel) {
      if (!this.shell || !panel.hasAttribute('data-gs-header-animated')) return;
      const bottom = Math.round(this.shell.getBoundingClientRect().bottom);
      panel.style.setProperty('--gs-hdr-panel-top', `${bottom + 12}px`);
    }

    _closeDropdowns(except) {
      this.querySelectorAll('[data-gs-header-dropdown-trigger][aria-expanded="true"]').forEach((t) => {
        if (t !== except) t.setAttribute('aria-expanded', 'false');
      });
    }

    _onClick(event) {
      const target = event.target;

      const panelTrigger = target.closest('[data-gs-header-panel-trigger]');
      if (panelTrigger) {
        event.preventDefault();
        this.togglePanel(panelTrigger.dataset.gsHeaderPanelTrigger, { trigger: panelTrigger });
        return;
      }

      if (target.closest('[data-gs-header-close]')) {
        this.closePanel();
        return;
      }

      const dropdownTrigger = target.closest('[data-gs-header-dropdown-trigger]');
      if (dropdownTrigger) {
        const open = dropdownTrigger.getAttribute('aria-expanded') !== 'true';
        this._closeDropdowns(dropdownTrigger);
        if (open) this.closePanel({ restoreFocus: false });
        dropdownTrigger.setAttribute('aria-expanded', String(open));
        return;
      }

      const sheetTrigger = target.closest('[data-gs-sheet-trigger]');
      if (sheetTrigger) {
        event.preventDefault();
        this._toggleSheetAccordion(sheetTrigger);
        return;
      }

      const copyButton = target.closest('[data-gs-header-copy]');
      if (copyButton) this._copyCode(copyButton);
    }

    _onKeydown(event) {
      if ((event.key === 'Enter' || event.key === ' ') && event.target.matches && event.target.matches('strong[data-gs-header-copy]')) {
        event.preventDefault();
        this._copyCode(event.target);
        return;
      }
      if (event.key === 'Escape') {
        const openDropdown = this.querySelector('[data-gs-header-dropdown-trigger][aria-expanded="true"]');
        if (openDropdown) {
          openDropdown.setAttribute('aria-expanded', 'false');
          openDropdown.focus();
          return;
        }
        if (this.activePanel) {
          event.stopPropagation();
          this.closePanel();
        }
        return;
      }
      if (event.key === 'Tab' && this.activePanel === 'mobile') this._trapFocus(event);
    }

    _onPointerDown(event) {
      if (!this.shell || this.shell.contains(event.target)) return;
      if (this.activePanel) this.closePanel({ restoreFocus: false });
      this._closeDropdowns();
    }

    _onBreakpoint(event) {
      if (event.matches && this.activePanel === 'mobile') this.closePanel({ restoreFocus: false });
      if (!event.matches && this.activePanel === 'mega') this.closePanel({ restoreFocus: false });
    }

    _onResize() {
      if (this.activePanel !== 'mobile') return;
      this._syncMobileOffset(this.getPanel('mobile'));
    }

    _trapFocus(event) {
      const nodes = Array.from(
        this.shell.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), summary')
      ).filter((el) => el.offsetParent !== null);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /**
     * Generic nested-accordion toggle for the mobile sheet
     * (sections/gs-header.liquid `.gs-hdr-sheet__*`). A trigger's
     * `aria-controls` points at its `[data-gs-sheet-panel]`; triggers are
     * grouped by their nearest `[data-gs-sheet-group]` ancestor so opening
     * one closes its siblings (single-open per level, independent per
     * nesting level). Category triggers (`data-gs-mobile-cat`) also fire
     * `gs-header:mobile-category` so assets/gs-header-megamenu.js keeps the
     * mobile featured-card slot in sync, same as before.
     */
    _toggleSheetAccordion(trigger) {
      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId && this.querySelector(`#${panelId}`);
      if (!panel) return;
      const willOpen = trigger.getAttribute('aria-expanded') !== 'true';
      const group = trigger.closest('[data-gs-sheet-group]');
      if (group) {
        Array.from(group.children).forEach((item) => {
          const t = item.querySelector(':scope > [data-gs-sheet-trigger]');
          const p = item.querySelector(':scope > [data-gs-sheet-panel]');
          if (t && t !== trigger) {
            t.setAttribute('aria-expanded', 'false');
            if (t.dataset.gsMobileCat) t.classList.remove('is-active');
          }
          if (p && p !== panel) p.hidden = true;
        });
      }
      trigger.setAttribute('aria-expanded', String(willOpen));
      panel.hidden = !willOpen;
      if (trigger.dataset.gsMobileCat) {
        trigger.classList.toggle('is-active', willOpen);
        this.dispatchEvent(new CustomEvent('gs-header:mobile-category', {
          bubbles: true,
          detail: { id: willOpen ? trigger.dataset.gsMobileCat : null },
        }));
      }
    }

    _onCartUpdate() {
      fetch(`${(window.routes && window.routes.cart_url) || '/cart'}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => this.setCartCount(cart.item_count))
        .catch(() => {});
    }

    setCartCount(count) {
      const el = this.querySelector('[data-gs-header-cart-count]');
      if (!el) return;
      el.textContent = count;
      el.classList.toggle('is-empty', !count);
    }

    _copyCode(button) {
      const code = button.dataset.code;
      const wrap = button.closest('[data-gs-header-copy-wrap]');
      const texts = Array.from((wrap || button).querySelectorAll('[data-gs-header-copy-text]'));
      const status = wrap && wrap.querySelector('[data-gs-header-copy-status]');
      const done = () => {
        texts.forEach((t) => {
          if (t.dataset.original === undefined) t.dataset.original = t.textContent;
          t.textContent = (wrap && wrap.dataset.copiedText) || 'COPIED!';
        });
        wrap && wrap.classList.add('is-copied');
        if (status) status.textContent = (wrap && wrap.dataset.copiedText) || 'Copied';
        clearTimeout(this._copyTimer);
        this._copyTimer = setTimeout(() => {
          texts.forEach((t) => {
            t.textContent = t.dataset.original;
            delete t.dataset.original;
          });
          wrap && wrap.classList.remove('is-copied');
          if (status) status.textContent = '';
        }, 1600);
      };
      const fallback = () => {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
        document.body.appendChild(ta);
        ta.select();
        try {
          if (document.execCommand('copy')) done();
        } catch (e) {
          /* copy unsupported */
        }
        document.body.removeChild(ta);
        button.focus();
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(done, fallback);
      } else {
        fallback();
      }
    }
  }

  customElements.define('gs-header', GSHeader);
}
